import { Group, Vector3 } from "three";
import { CropPlaneToolConfig as Config } from "../Config/CropPlaneToolConfig.js";
import { LimitUtils } from "../Utils/LimitUtils.js";
import { VectorUtils } from "../Utils/VectorUtils.js";
import { DraggablePoint } from "./Primitive/DraggablePoint.js";
import { LinePointToPoint } from "./Primitive/LinePointToPoint.js";
/**
 * Tool that allow to crop the plane.
 */
export class CropPlaneTool extends Group /*implements Tool*/ {
    constructor(viewType, plane) {
        super();
        this._planeA = new Vector3();
        this._planeB = new Vector3();
        this._planeC = new Vector3();
        this._planeD = new Vector3();
        this._planeHorDir = new Vector3();
        this._planeVerDir = new Vector3();
        this._minSize = 15; //add to config
        this.name = "CropPlaneTool";
        this._plane = plane;
        this.add(this.A = new DraggablePoint("A", viewType, Config.A_color), this.B = new DraggablePoint("B", viewType, Config.B_color), this.C = new DraggablePoint("C", viewType, Config.C_color), this.D = new DraggablePoint("D", viewType, Config.D_color), this.ABLine = new LinePointToPoint("ACLine", Config.ABLine_color, this.A, this.B), this.BCLine = new LinePointToPoint("BCLine", Config.BCLine_color, this.B, this.C), this.CDLine = new LinePointToPoint("CDLine", Config.CDLine_color, this.C, this.D), this.DALine = new LinePointToPoint("DALine", Config.DALine_color, this.D, this.A));
        this.bindEvents();
        this.visible = false;
    }
    /**
     * Starts the tool execution.
     */
    start() {
        this.updatePlaneVertices();
        this.updateVertices();
        this.visible = true;
    }
    /**
     * Ebds the tool execution.
     */
    confirm() {
        this._plane.updateCenterTranslation();
        this.visible = false;
    }
    bindEvents() {
        this.bindEvent("onDragMove", (args) => {
            args.preventDefault = true;
            this.updateVertices();
        });
        this.A.bindEvent("onDragMove", (args) => {
            const planeCenter = this.A.position.clone().lerp(this.C.position, 0.5);
            LimitUtils.applyLimitAndProjectVector(this._planeA, this._planeB, args.position, planeCenter);
            LimitUtils.applyLimitAndProjectVector(this._planeA, this._planeD, args.position, planeCenter);
            LimitUtils.applyLimitAndProjectVector(this.C.position, this.B.position, args.position, planeCenter, this._minSize);
            LimitUtils.applyLimitAndProjectVector(this.C.position, this.D.position, args.position, planeCenter, this._minSize);
            this._plane[this._plane.horizontalFlip ? "clipFromRight" : "clipFromLeft"] = this._planeA.distanceTo(VectorUtils.projectOnVector(args.position, this._planeA, this._planeB)) / this._planeWidth;
            this._plane[this._plane.verticalFlip ? "clipFromBottom" : "clipFromTop"] = this._planeA.distanceTo(VectorUtils.projectOnVector(args.position, this._planeA, this._planeD)) / this._planeHeight;
        });
        this.B.bindEvent("onDragMove", (args) => {
            const planeCenter = this.B.position.clone().lerp(this.D.position, 0.5);
            LimitUtils.applyLimitAndProjectVector(this._planeB, this._planeA, args.position, planeCenter);
            LimitUtils.applyLimitAndProjectVector(this._planeB, this._planeC, args.position, planeCenter);
            LimitUtils.applyLimitAndProjectVector(this.D.position, this.A.position, args.position, planeCenter, this._minSize);
            LimitUtils.applyLimitAndProjectVector(this.D.position, this.C.position, args.position, planeCenter, this._minSize);
            this._plane[this._plane.horizontalFlip ? "clipFromLeft" : "clipFromRight"] = this._planeB.distanceTo(VectorUtils.projectOnVector(args.position, this._planeA, this._planeB)) / this._planeWidth;
            this._plane[this._plane.verticalFlip ? "clipFromBottom" : "clipFromTop"] = this._planeA.distanceTo(VectorUtils.projectOnVector(args.position, this._planeA, this._planeD)) / this._planeHeight;
        });
        this.C.bindEvent("onDragMove", (args) => {
            const planeCenter = this.C.position.clone().lerp(this.A.position, 0.5);
            LimitUtils.applyLimitAndProjectVector(this._planeC, this._planeB, args.position, planeCenter);
            LimitUtils.applyLimitAndProjectVector(this._planeC, this._planeD, args.position, planeCenter);
            LimitUtils.applyLimitAndProjectVector(this.A.position, this.B.position, args.position, planeCenter, this._minSize);
            LimitUtils.applyLimitAndProjectVector(this.A.position, this.D.position, args.position, planeCenter, this._minSize);
            this._plane[this._plane.horizontalFlip ? "clipFromLeft" : "clipFromRight"] = this._planeB.distanceTo(VectorUtils.projectOnVector(args.position, this._planeA, this._planeB)) / this._planeWidth;
            this._plane[this._plane.verticalFlip ? "clipFromTop" : "clipFromBottom"] = this._planeD.distanceTo(VectorUtils.projectOnVector(args.position, this._planeA, this._planeD)) / this._planeHeight;
        });
        this.D.bindEvent("onDragMove", (args) => {
            const planeCenter = this.D.position.clone().lerp(this.B.position, 0.5);
            LimitUtils.applyLimitAndProjectVector(this._planeD, this._planeA, args.position, planeCenter);
            LimitUtils.applyLimitAndProjectVector(this._planeD, this._planeC, args.position, planeCenter);
            LimitUtils.applyLimitAndProjectVector(this.B.position, this.A.position, args.position, planeCenter, this._minSize);
            LimitUtils.applyLimitAndProjectVector(this.B.position, this.C.position, args.position, planeCenter, this._minSize);
            this._plane[this._plane.horizontalFlip ? "clipFromRight" : "clipFromLeft"] = this._planeA.distanceTo(VectorUtils.projectOnVector(args.position, this._planeA, this._planeB)) / this._planeWidth;
            this._plane[this._plane.verticalFlip ? "clipFromTop" : "clipFromBottom"] = this._planeD.distanceTo(VectorUtils.projectOnVector(args.position, this._planeA, this._planeD)) / this._planeHeight;
        });
    }
    updatePlaneVertices() {
        const { A, B, C, D } = this._plane.getNoClippedVertices(0);
        this._planeA.copy(A);
        this._planeB.copy(B);
        this._planeC.copy(C);
        this._planeD.copy(D);
        this._planeHorDir.copy(A.clone().sub(B));
        this._planeVerDir.copy(A.clone().sub(D));
        this._planeWidth = A.distanceTo(B);
        this._planeHeight = A.distanceTo(D);
    }
    updateVertices() {
        const { A, B, C, D } = this._plane.getVertices(0);
        this.A.position.copy(A);
        this.B.position.copy(B);
        this.C.position.copy(C);
        this.D.position.copy(D);
    }
}
//# sourceMappingURL=CropPlaneTool.js.map